/**
 * Phase 3 — compact stores for RevisionCloud, Wipeout and AttDef.
 *
 * - RevisionCloud — closed polygonal/rectangular boundary; vertices share
 *   `polylineVertexSchema` (x, y, bulge, startWidth, endWidth, vertexId?)
 *   identical to LWPOLYLINE. Boundary vertex coordinates are packed into a
 *   CSR-like Float32Array buffer mirroring `CompactLwpolylineStore`.
 * - Wipeout — closed polygon boundary; vertices are bare 2D points (x, y).
 *   Packed as `Float32Array(2*M)` per Phase 3 contract.
 * - AttDef — identical schema to TEXT (insertionPoint, height, rotation,
 *   contents, styleName, …). Block-definition child only; never appears in
 *   model/paper space directly. Wiring through `populateCompactCache` is
 *   per-space and therefore does not visit AttDef today — this is
 *   documented as a known limitation in the aggregateStore comment. The
 *   per-type store is in place so that any future block-children populate
 *   path can dispatch through `appendEntity` with no further changes.
 */

import type {
  AttdefEntity,
  AttdefFlags,
  DrawingPoint3D,
  RevisionCloudEntity,
  WipeoutEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  BASE_BYTES_PER_ENTITY,
  createBaseColumns,
  growBaseColumns,
  readBase,
  writeBase,
  type BaseColumns,
} from "./compactStoreBase";
import {
  POLYLINE_VERTEX_FLOATS,
  packPolylineVertices,
  unpackPolylineVertices,
} from "./compactVertexPacking";
import {
  internString,
  lookupString,
  type SharedStringTable,
} from "./sharedStringTable";

/* ── REVISION CLOUD ───────────────────────────────────────────────── */

// arcChordLength, approximateArcLength, constantWidth (43), elevation (38),
// thickness (39) — S-REVCLOUD-FIELDS.
const REVCLOUD_FIXED_FLOATS = 5;
const REVCLOUD_INTS = 2; // styleName intern id, creationMode tag
const FLAG_RC_CLOSED = 1;

// DXF group 210/220/230 OCS normal (S-REVCLOUD-FIELDS). Stored sparsely (only
// when non-identity) like the ellipse extrusion, so the absent/(0,0,1) default
// stays absent on the reconstructed model.
function isRcDefaultExtrusion(e: DrawingPoint3D | undefined): boolean {
  if (!e) return true;
  return e.x === 0 && e.y === 0 && e.z === 1;
}

const REVCLOUD_CREATION_MODES = ["Rectangular", "Polygonal", "Object"] as const;

function revcloudCreationModeTag(
  mode: RevisionCloudEntity["creationMode"],
): number {
  const idx = REVCLOUD_CREATION_MODES.indexOf(mode);
  return idx < 0 ? 0 : idx;
}

function tagToRevcloudCreationMode(
  tag: number,
): RevisionCloudEntity["creationMode"] {
  return REVCLOUD_CREATION_MODES[tag] ?? "Rectangular";
}

export interface CompactRevisionCloudStore {
  count: number;
  base: BaseColumns;
  fixed: Float32Array; // [arcChordLength, approximateArcLength] per slot
  ints: Uint16Array; // [styleNameInternId, creationModeTag] per slot
  flags: Uint8Array; // closed bit
  vertexData: Float32Array; // packed REVCLOUD_VERTEX_FLOATS per vertex
  vertexOffsets: Uint32Array; // capacity+1 — [start, end) into vertexData
  vertexIdData: Map<number, number[]>; // optional per-vertex vertexId arrays
  extrusions: Map<number, DrawingPoint3D>; // sparse OCS normal (S-REVCLOUD-FIELDS)
}

export function createCompactRevisionCloudStore(
  initialCapacity = 64,
): CompactRevisionCloudStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    fixed: new Float32Array(cap * REVCLOUD_FIXED_FLOATS),
    ints: new Uint16Array(cap * REVCLOUD_INTS),
    flags: new Uint8Array(cap),
    vertexData: new Float32Array(512),
    vertexOffsets: new Uint32Array(cap + 1),
    vertexIdData: new Map(),
    extrusions: new Map(),
  };
}

function ensureRevCloudEntityCap(
  store: CompactRevisionCloudStore,
  needed: number,
): void {
  if (needed <= store.base.capacity) return;
  const newCap = growBaseColumns(store.base, needed);
  const nfx = new Float32Array(newCap * REVCLOUD_FIXED_FLOATS);
  nfx.set(store.fixed);
  const ni = new Uint16Array(newCap * REVCLOUD_INTS);
  ni.set(store.ints);
  const nfl = new Uint8Array(newCap);
  nfl.set(store.flags);
  const no = new Uint32Array(newCap + 1);
  no.set(store.vertexOffsets);
  store.fixed = nfx;
  store.ints = ni;
  store.flags = nfl;
  store.vertexOffsets = no;
}

export function appendRevisionCloud(
  store: CompactRevisionCloudStore,
  table: SharedStringTable,
  entity: RevisionCloudEntity,
): number {
  const index = store.count;
  ensureRevCloudEntityCap(store, index + 1);
  store.count++;

  const fx = index * REVCLOUD_FIXED_FLOATS;
  store.fixed[fx + 0] = entity.arcChordLength;
  store.fixed[fx + 1] = entity.approximateArcLength;
  store.fixed[fx + 2] = entity.constantWidth;
  store.fixed[fx + 3] = entity.elevation;
  store.fixed[fx + 4] = entity.thickness;
  if (isRcDefaultExtrusion(entity.extrusion)) store.extrusions.delete(index);
  else store.extrusions.set(index, { ...entity.extrusion! });
  const ii = index * REVCLOUD_INTS;
  store.ints[ii + 0] = internString(table, entity.revisionCloudStyle);
  store.ints[ii + 1] = revcloudCreationModeTag(entity.creationMode) & 0xffff;
  store.flags[index] = entity.closed ? FLAG_RC_CLOSED : 0;
  writeBase(store.base, table, index, entity);

  // REVCLOUD shares the LWPOLYLINE CSR vertex layout (5 floats + sparse
  // vertexId Map) — pack via the shared one-truth helper.
  packPolylineVertices(store, index, entity.vertices);
  return index;
}

export function revisionCloudFromCompact(
  store: CompactRevisionCloudStore,
  table: SharedStringTable,
  index: number,
): RevisionCloudEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `revisionCloudFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const fx = index * REVCLOUD_FIXED_FLOATS;
  const ii = index * REVCLOUD_INTS;
  const flags = store.flags[index] ?? 0;
  const vertices = unpackPolylineVertices(store, index);
  const result: RevisionCloudEntity = {
    ...readBase(store.base, table, index),
    type: "Revision Cloud",
    vertices,
    closed: (flags & FLAG_RC_CLOSED) !== 0,
    arcChordLength: store.fixed[fx + 0]!,
    approximateArcLength: store.fixed[fx + 1]!,
    revisionCloudStyle: lookupString(table, store.ints[ii + 0]!),
    creationMode: tagToRevcloudCreationMode(store.ints[ii + 1]!),
    constantWidth: store.fixed[fx + 2]!,
    elevation: store.fixed[fx + 3]!,
    thickness: store.fixed[fx + 4]!,
  };
  const extrusion = store.extrusions.get(index);
  if (extrusion) result.extrusion = extrusion;
  return result;
}

export function estimateRevisionCloudBytesPerEntity(
  store: CompactRevisionCloudStore,
): number {
  const fixed =
    REVCLOUD_FIXED_FLOATS * 4 +
    REVCLOUD_INTS * 2 +
    1 +
    4 +
    BASE_BYTES_PER_ENTITY;
  if (store.count === 0) return fixed;
  const totalVertices = store.vertexOffsets[store.count]!;
  const variable =
    (totalVertices * POLYLINE_VERTEX_FLOATS * 4) / store.count;
  return fixed + variable;
}

/* ── WIPEOUT ──────────────────────────────────────────────────────── */

const WIPEOUT_VERTEX_FLOATS = 2; // x, y (drawingPointSchema)
const FLAG_WP_FRAME = 1;
const FLAG_WP_CLIP_INSIDE = 2; // clipMode 1 (inside); absent = 0 (outside)

export interface CompactWipeoutStore {
  count: number;
  base: BaseColumns;
  flags: Uint8Array; // frame bit
  vertexData: Float32Array; // 2*M floats packed
  vertexOffsets: Uint32Array; // capacity+1 — [start, end) into vertexData
}

export function createCompactWipeoutStore(
  initialCapacity = 32,
): CompactWipeoutStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    flags: new Uint8Array(cap),
    vertexData: new Float32Array(256),
    vertexOffsets: new Uint32Array(cap + 1),
  };
}

function ensureWipeoutEntityCap(
  store: CompactWipeoutStore,
  needed: number,
): void {
  if (needed <= store.base.capacity) return;
  const newCap = growBaseColumns(store.base, needed);
  const nfl = new Uint8Array(newCap);
  nfl.set(store.flags);
  const no = new Uint32Array(newCap + 1);
  no.set(store.vertexOffsets);
  store.flags = nfl;
  store.vertexOffsets = no;
}

function ensureWipeoutVertexCap(
  store: CompactWipeoutStore,
  needed: number,
): void {
  if (needed <= store.vertexData.length) return;
  let cap = Math.max(store.vertexData.length, 1);
  while (cap < needed) cap *= 2;
  const nu = new Float32Array(cap);
  nu.set(store.vertexData);
  store.vertexData = nu;
}

export function appendWipeout(
  store: CompactWipeoutStore,
  table: SharedStringTable,
  entity: WipeoutEntity,
): number {
  const index = store.count;
  ensureWipeoutEntityCap(store, index + 1);
  store.count++;

  store.flags[index] =
    (entity.frame ? FLAG_WP_FRAME : 0) |
    (entity.clipMode === 1 ? FLAG_WP_CLIP_INSIDE : 0);
  writeBase(store.base, table, index, entity);

  const startVertex = index === 0 ? 0 : store.vertexOffsets[index]!;
  const vertexFloats = entity.vertices.length * WIPEOUT_VERTEX_FLOATS;
  ensureWipeoutVertexCap(
    store,
    startVertex * WIPEOUT_VERTEX_FLOATS + vertexFloats,
  );
  for (let i = 0; i < entity.vertices.length; i++) {
    const v = entity.vertices[i]!;
    const off = (startVertex + i) * WIPEOUT_VERTEX_FLOATS;
    store.vertexData[off + 0] = v.x;
    store.vertexData[off + 1] = v.y;
  }
  store.vertexOffsets[index] = startVertex;
  store.vertexOffsets[index + 1] = startVertex + entity.vertices.length;
  return index;
}

export function wipeoutFromCompact(
  store: CompactWipeoutStore,
  table: SharedStringTable,
  index: number,
): WipeoutEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `wipeoutFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const flags = store.flags[index] ?? 0;
  const startVertex = store.vertexOffsets[index]!;
  const endVertex = store.vertexOffsets[index + 1]!;
  const vertices: WipeoutEntity["vertices"] = [];
  for (let i = 0; i < endVertex - startVertex; i++) {
    const off = (startVertex + i) * WIPEOUT_VERTEX_FLOATS;
    vertices.push({
      x: store.vertexData[off + 0]!,
      y: store.vertexData[off + 1]!,
    });
  }
  return {
    ...readBase(store.base, table, index),
    type: "Wipeout",
    vertices,
    frame: (flags & FLAG_WP_FRAME) !== 0,
    clipMode: (flags & FLAG_WP_CLIP_INSIDE) !== 0 ? 1 : 0,
  };
}

export function estimateWipeoutBytesPerEntity(
  store: CompactWipeoutStore,
): number {
  const fixed = 1 + 4 + BASE_BYTES_PER_ENTITY;
  if (store.count === 0) return fixed;
  const totalVertices = store.vertexOffsets[store.count]!;
  const variable = (totalVertices * WIPEOUT_VERTEX_FLOATS * 4) / store.count;
  return fixed + variable;
}

/* ── ATTDEF ───────────────────────────────────────────────────────── */
// AttDef mirrors TEXT plus attdef-specific tag/prompt/defaultValue/handle
// and the AttdefFlags struct (invisible, constant, verify, preset).

const ATTDEF_FLOATS = 6; // insertion x/y, height, rotation, widthFactor, obliqueAngle
const ATTDEF_INTS = 9;
// 0: styleName, 1: tag, 2: prompt, 3: defaultValue, 4: handle, 5: backgroundColor
// 6: hJustification, 7: vJustification, 8: generationFlags (lower 16)
const ATTDEF_BG_FLOATS = 1; // fillBoxScale
const FLAG_ADEF_INVISIBLE = 1;
const FLAG_ADEF_CONSTANT = 2;
const FLAG_ADEF_VERIFY = 4;
const FLAG_ADEF_PRESET = 8;
const FLAG_ADEF_LOCKPOS = 16;
const FLAG_ADEF_MULTILINE = 32;
const FLAG_ADEF_BG = 64;
/**
 * Phase 3 followup (codex P2) — sentinel for "backgroundColor is
 * present (and may be empty string)". The encoder writes
 * `entity.backgroundColor ?? ""` into the interned-string slot, so an
 * absent `backgroundColor` and a `backgroundColor: ""` both encoded as
 * the empty string. Decode then restored `backgroundColor` only when
 * the slot was non-empty — which collapsed real `""` values to
 * `undefined` on round-trip. The new flag bit distinguishes the two:
 *
 *   - bit set, slot "" → `backgroundColor: ""`
 *   - bit set, slot "X" → `backgroundColor: "X"`
 *   - bit unset → `backgroundColor` omitted (undefined)
 *
 * Bit 128 is the next available slot in the `flags: Uint8Array` byte
 * (bits 1, 2, 4, 8, 16, 32, 64 already in use above).
 */
const FLAG_ADEF_HAS_BG_COLOR = 128;

const DEFAULT_ATTDEF_FLAGS: AttdefFlags = {
  invisible: false,
  constant: false,
  verify: false,
  preset: false,
  lockPosition: false,
  multiline: false,
};

function attdefFlagsToBits(flags: AttdefFlags | undefined): number {
  const f = flags ?? DEFAULT_ATTDEF_FLAGS;
  let bits = 0;
  if (f.invisible) bits |= FLAG_ADEF_INVISIBLE;
  if (f.constant) bits |= FLAG_ADEF_CONSTANT;
  if (f.verify) bits |= FLAG_ADEF_VERIFY;
  if (f.preset) bits |= FLAG_ADEF_PRESET;
  if (f.lockPosition) bits |= FLAG_ADEF_LOCKPOS;
  if (f.multiline) bits |= FLAG_ADEF_MULTILINE;
  return bits;
}

function bitsToAttdefFlags(bits: number): AttdefFlags {
  return {
    invisible: (bits & FLAG_ADEF_INVISIBLE) !== 0,
    constant: (bits & FLAG_ADEF_CONSTANT) !== 0,
    verify: (bits & FLAG_ADEF_VERIFY) !== 0,
    preset: (bits & FLAG_ADEF_PRESET) !== 0,
    lockPosition: (bits & FLAG_ADEF_LOCKPOS) !== 0,
    multiline: (bits & FLAG_ADEF_MULTILINE) !== 0,
  };
}

export interface CompactAttdefStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Uint16Array;
  bgFloats: Float32Array;
  /** Combined AttdefFlags (low nibble) + backgroundFill (bit 4). */
  flags: Uint8Array;
  alignmentPoints: Map<number, { x: number; y: number }>;
  annotativeContexts: Map<number, AttdefEntity["annotativeContexts"]>;
  creationScaleNames: Map<number, string>;
}

export function createCompactAttdefStore(
  initialCapacity = 64,
): CompactAttdefStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * ATTDEF_FLOATS),
    ints: new Uint16Array(cap * ATTDEF_INTS),
    bgFloats: new Float32Array(cap * ATTDEF_BG_FLOATS),
    flags: new Uint8Array(cap),
    alignmentPoints: new Map(),
    annotativeContexts: new Map(),
    creationScaleNames: new Map(),
  };
}

function ensureAttdefCap(store: CompactAttdefStore, needed: number): void {
  if (needed <= store.base.capacity) return;
  const newCap = growBaseColumns(store.base, needed);
  const nf = new Float32Array(newCap * ATTDEF_FLOATS);
  nf.set(store.floats);
  const ni = new Uint16Array(newCap * ATTDEF_INTS);
  ni.set(store.ints);
  const nbf = new Float32Array(newCap * ATTDEF_BG_FLOATS);
  nbf.set(store.bgFloats);
  const nfl = new Uint8Array(newCap);
  nfl.set(store.flags);
  store.floats = nf;
  store.ints = ni;
  store.bgFloats = nbf;
  store.flags = nfl;
}

export function appendAttdef(
  store: CompactAttdefStore,
  table: SharedStringTable,
  entity: AttdefEntity,
): number {
  const index = store.count;
  ensureAttdefCap(store, index + 1);
  store.count++;

  const f = index * ATTDEF_FLOATS;
  store.floats[f + 0] = entity.insertionPoint.x;
  store.floats[f + 1] = entity.insertionPoint.y;
  store.floats[f + 2] = entity.height;
  store.floats[f + 3] = entity.rotation;
  store.floats[f + 4] = entity.widthFactor;
  store.floats[f + 5] = entity.obliqueAngle;

  const ii = index * ATTDEF_INTS;
  store.ints[ii + 0] = internString(table, entity.styleName);
  store.ints[ii + 1] = internString(table, entity.tag);
  store.ints[ii + 2] = internString(table, entity.prompt);
  store.ints[ii + 3] = internString(table, entity.defaultValue);
  store.ints[ii + 4] = internString(table, entity.handle);
  // Phase 3 followup (codex P2) — write the empty string when
  // `backgroundColor` is absent OR explicitly `""`. The presence bit
  // below tracks the distinction so decode can restore `""` vs
  // `undefined` correctly.
  store.ints[ii + 5] = internString(table, entity.backgroundColor ?? "");
  store.ints[ii + 6] = entity.hJustification & 0xffff;
  store.ints[ii + 7] = entity.vJustification & 0xffff;
  store.ints[ii + 8] = entity.generationFlags & 0xffff;

  store.bgFloats[index * ATTDEF_BG_FLOATS + 0] = entity.fillBoxScale;

  let bits = attdefFlagsToBits(entity.flags);
  if (entity.backgroundFill) bits |= FLAG_ADEF_BG;
  // Phase 3 followup (codex P2) — encode "has backgroundColor" so
  // `""` round-trips as `""` instead of collapsing to `undefined`.
  if (entity.backgroundColor !== undefined) bits |= FLAG_ADEF_HAS_BG_COLOR;
  store.flags[index] = bits;
  writeBase(store.base, table, index, entity);

  if (entity.alignmentPoint !== undefined) {
    store.alignmentPoints.set(index, { ...entity.alignmentPoint });
  }
  if (entity.annotativeContexts.length > 0) {
    store.annotativeContexts.set(
      index,
      entity.annotativeContexts.map((c) => ({ ...c })),
    );
  }
  if (entity.creationScaleName !== undefined) {
    store.creationScaleNames.set(index, entity.creationScaleName);
  }
  return index;
}

export function attdefFromCompact(
  store: CompactAttdefStore,
  table: SharedStringTable,
  index: number,
): AttdefEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `attdefFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const f = index * ATTDEF_FLOATS;
  const ii = index * ATTDEF_INTS;
  const bits = store.flags[index] ?? 0;
  const bgColorStr = lookupString(table, store.ints[ii + 5]!);
  const result: AttdefEntity = {
    ...readBase(store.base, table, index),
    type: "Attdef",
    handle: lookupString(table, store.ints[ii + 4]!),
    tag: lookupString(table, store.ints[ii + 1]!),
    prompt: lookupString(table, store.ints[ii + 2]!),
    defaultValue: lookupString(table, store.ints[ii + 3]!),
    insertionPoint: { x: store.floats[f + 0]!, y: store.floats[f + 1]! },
    height: store.floats[f + 2]!,
    rotation: store.floats[f + 3]!,
    styleName: lookupString(table, store.ints[ii + 0]!),
    widthFactor: store.floats[f + 4]!,
    obliqueAngle: store.floats[f + 5]!,
    generationFlags: store.ints[ii + 8]!,
    hJustification: store.ints[ii + 6]!,
    vJustification: store.ints[ii + 7]!,
    flags: bitsToAttdefFlags(bits),
    backgroundFill: (bits & FLAG_ADEF_BG) !== 0,
    fillBoxScale: store.bgFloats[index * ATTDEF_BG_FLOATS + 0]!,
    annotativeContexts: store.annotativeContexts.get(index) ?? [],
  };
  // Phase 3 followup (codex P2) — restore `backgroundColor` from the
  // (flag, slot) pair so the three cases round-trip exactly:
  //   - flag set, slot non-empty → `backgroundColor: <value>`
  //   - flag set, slot empty     → `backgroundColor: ""`
  //   - flag unset               → omit `backgroundColor` (undefined)
  if ((bits & FLAG_ADEF_HAS_BG_COLOR) !== 0) result.backgroundColor = bgColorStr;
  const align = store.alignmentPoints.get(index);
  if (align !== undefined) result.alignmentPoint = align;
  const csn = store.creationScaleNames.get(index);
  if (csn !== undefined) result.creationScaleName = csn;
  return result;
}

export function estimateAttdefBytesPerEntity(): number {
  return (
    ATTDEF_FLOATS * 4 +
    ATTDEF_INTS * 2 +
    ATTDEF_BG_FLOATS * 4 +
    1 +
    BASE_BYTES_PER_ENTITY
  );
}

